import React, { useEffect, useRef, useState } from "react";
import { FaComments, FaPaperPlane, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import axiosInstance from "../Utils/axiosInstance";
import { toast } from "react-hot-toast";

const ChatWidget = ({ onStateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [selectedPlatformById, setSelectedPlatformById] = useState({});
  
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I can help with everything on this site: find games, list categories, prices/platforms, latest games, and answer questions about wishlist, cart, payments, privacy and terms.",
      results: [],
      suggestions: [
        "Recommend some games",
        "List categories",
        "Windows games under $200",
        "Show latest games",
        "What's in my wishlist?",
        "What's in my cart?",
        "Show my orders",
        "Privacy policy",
        "Terms & conditions",
        "Contact support",
      ],
      id: "welcome",
    },
  ]);
  const scrollRef = useRef(null);

  // Notify parent component when chat state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(isOpen);
    }
  }, [isOpen, onStateChange]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Persist chat locally
  useEffect(() => {
    try {
      localStorage.setItem("yoyo_chat_messages", JSON.stringify(messages.slice(-50)));
    } catch (_) { }
  }, [messages]);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("yoyo_chat_messages");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) {
          setMessages(parsed);
        }
      }
    } catch (_) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch dynamic welcome suggestions from backend when opening
  useEffect(() => {
    const fetchWelcome = async () => {
      try {
        const res = await axiosInstance.get(`/chat`, { params: {} });
        const data = res?.data || {};
        if (data?.reply || data?.suggestions) {
          setMessages([
            {
              role: "assistant",
              content: data.reply || messages[0]?.content,
              results: Array.isArray(data.results) ? data.results : [],
              suggestions: Array.isArray(data.suggestions) ? data.suggestions : messages[0]?.suggestions,
              id: "welcome-remote",
            },
          ]);
        }
      } catch (_) {
        // ignore welcome fetch error
      }
    };
    if (isOpen && messages.length === 1 && messages[0]?.id === "welcome") {
      fetchWelcome();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sendMessage = async (opts = {}) => {
    const q = input.trim();
    if ((!q && !opts.q) || isLoading) return;
    const effectiveQ = opts.q || q;
    if (!opts.append) {
      setMessages((prev) => [...prev, { role: "user", content: effectiveQ, id: Date.now() + "u" }]);
    }
    setInput("");
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`/chat`, { params: { q: effectiveQ, page: opts.page || 1, limit: 6 } });
      const data = res?.data || {};
      setLastQuery(data.q || effectiveQ);
      setPage(data.page || 1);
      setHasMore(Boolean(data.hasMore));
      if (opts.append) {
        setMessages((prev) => {
          const copy = [...prev];
          // append results to last assistant message if present
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].role === "assistant") {
              const merged = Array.isArray(copy[i].results) ? [...copy[i].results] : [];
              const incoming = Array.isArray(data.results) ? data.results : [];
              copy[i] = {
                ...copy[i],
                content: data.reply || copy[i].content,
                results: [...merged, ...incoming],
                suggestions: Array.isArray(data.suggestions) ? data.suggestions : copy[i].suggestions,
              };
              return copy;
            }
          }
          // fallback: push new assistant message
          return [
            ...copy,
            {
              role: "assistant",
              content: data.reply || "",
              results: Array.isArray(data.results) ? data.results : [],
              suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
              id: Date.now() + "a",
            },
          ];
        });
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.reply || "",
            results: Array.isArray(data.results) ? data.results : [],
            suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
            id: Date.now() + "a",
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I couldn't reach the assistant. Please try again.",
          results: [],
          id: Date.now() + "e",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: "assistant",
        content:
          "Hi! I can help with everything on this site: find games, list categories, prices/platforms, latest games, and answer questions about wishlist, cart, payments, privacy and terms.",
        results: [],
        suggestions: [
          "Recommend some games",
          "List categories",
          "Windows games under $200",
          "Show latest games",
          "What's in my wishlist?",
          "What's in my cart?",
          "Show my orders",
          "Privacy policy",
          "Terms & conditions",
          "Contact support",
        ],
        id: "welcome",
      },
    ]);
    setLastQuery("");
    setPage(1);
    setHasMore(false);
    try { localStorage.removeItem("yoyo_chat_messages"); } catch (_) { }
  };

  const handleSelectPlatform = (gameId, platform) => {
    setSelectedPlatformById((prev) => ({ ...prev, [gameId]: platform }));
  };

  const addToWishlist = async (gameId) => {
    try {
      setActionLoading(`w-${gameId}`);
      await axiosInstance.post("/wishlist/add", { gameId });
      toast.success("Added to wishlist");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast("Please log in to use wishlist");
      else toast.error("Failed to add to wishlist");
    } finally {
      setActionLoading("");
    }
  };

  const addToCart = async (gameId, platforms) => {
    try {
      setActionLoading(`c-${gameId}`);
      const available = Object.entries(platforms || {})
        .filter(([, v]) => v)
        .map(([k]) => k);
      const selected = selectedPlatformById[gameId] || available[0];
      if (!selected) {
        toast("Select a platform first");
        return;
      }
      await axiosInstance.post("/cart/add", { gameId, platform: selected, qty: 1 });
      toast.success("Added to cart");
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.message;
      if (status === 401) toast("Please log in to add to cart");
      else toast.error(message || "Failed to add to cart");
    } finally {
      setActionLoading("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-16 right-5 z-50">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
          className="rounded-full p-3 bg-gradient-primary text-white shadow-lg hover:opacity-90 transition"
        >
          <FaComments size={20} />
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end justify-end z-50">
          <div className="w-[90vw] sm:w-96 h-[70vh] sm:h-[520px] lg:h-[650px] lg:w-[500px] bg-black/80 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl flex flex-col mr-8 mb-8">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/20">
              <div className="text-white font-semibold">Assistant</div>
              <div className="flex items-center gap-1">
                <button
                  onClick={clearChat}
                  aria-label="Clear chat"
                  className="text-white/70 hover:text-white text-xs px-2 py-1 border border-white/20 rounded mr-2"
                >
                  Clear
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close chat"
                  className="text-white/80 hover:text-white"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div ref={scrollRef} className="chatbox_Scroll flex-1 overflow-y-auto p-3 space-y-3">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user"
                      ? "bg-purple-600 text-white"
                      : "bg-white/10 text-white"
                      }`}
                  >
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    {m.results && m.results.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {m.results.map((r) => {
                          const available = Object.entries(r.platforms || {})
                            .filter(([, v]) => v)
                            .map(([k]) => k);
                          const selected = selectedPlatformById[r.id] || available[0] || "";
                          return (
                            <div key={r.id} className="p-2 rounded bg-white/5 hover:bg-white/10 transition">
                              <div className="flex items-center gap-2">
                                {r.coverImage && (
                                  <img src={r.coverImage} alt={r.title} className="w-10 h-12 object-cover rounded" />
                                )}
                                <div className="text-xs flex-1 min-w-0">
                                  <Link
                                    to={r.url}
                                    className="font-semibold text-white/90 hover:underline truncate block"
                                    onClick={() => setIsOpen(false)}
                                  >
                                    {r.title}
                                  </Link>
                                  {r.snippet && <div className="text-white/60 line-clamp-2">{r.snippet}</div>}
                                </div>
                              </div>
                              {available.length > 0 && (
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                  <select
                                    value={selected}
                                    onChange={(e) => handleSelectPlatform(r.id, e.target.value)}
                                    className="bg-white/10 text-white text-[11px] px-1 py-1 rounded border border-white/20"
                                  >
                                    {available.map((p) => (
                                      <option key={p} value={p} className="text-black">
                                        {p} {r.platforms?.[p]?.price != null ? `- $${r.platforms[p].price}` : ""}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => addToCart(r.id, r.platforms)}
                                    disabled={actionLoading === `c-${r.id}`}
                                    className="px-2 py-1 text-[11px] rounded bg-purple-600 text-white disabled:opacity-50"
                                  >
                                    {actionLoading === `c-${r.id}` ? "Adding..." : "Add to Cart"}
                                  </button>
                                  <button
                                    onClick={() => addToWishlist(r.id)}
                                    disabled={actionLoading === `w-${r.id}`}
                                    className="px-2 py-1 text-[11px] rounded bg-white/10 text-white border border-white/20 disabled:opacity-50"
                                  >
                                    {actionLoading === `w-${r.id}` ? "Adding..." : "Wishlist"}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {m === messages[messages.length - 1] && hasMore && (
                          <div className="pt-1">
                            <button
                              disabled={isLoading}
                              onClick={() => {
                                const nextPage = (page || 1) + 1;
                                setPage(nextPage);
                                sendMessage({ q: lastQuery, page: nextPage, append: true });
                              }}
                              className="w-full text-center text-xs px-3 py-2 rounded bg-white/10 hover:bg-white/20 text-white disabled:opacity-50"
                            >
                              {isLoading ? "Loading..." : "Show more"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {m.suggestions && m.suggestions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.suggestions.map((s, idx) => (
                          <button
                            key={`${m.id}-sugg-${idx}`}
                            onClick={() => {
                              setInput(s);
                              // small delay to allow textarea to update visually
                              setTimeout(() => sendMessage(), 0);
                            }}
                            className="px-2 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-3 py-2 text-sm bg-white/10 text-white">
                    Thinking...
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-white/20">
              <div className="flex items-center gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about games, platforms, or prices..."
                  rows={1}
                  className="flex-1 cursor-none resize-none rounded-lg bg-white/10 text-white placeholder-white/60 px-3 py-2 focus:outline-none"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  className="p-2 rounded-lg bg-gradient-primary text-white disabled:opacity-50"
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;


